import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
  Camera,
  ChevronRight,
  Database,
  Hash,
  Image as ImageIcon,
  Settings,
  Shield,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useBranding,
  useCompanySettings,
  useUpdateBranding,
  useUpdateCompanySettings,
} from '@/hooks/useSettings'
import { toast } from '@/components/ui/toast'
import {
  COUNTRIES,
  DEFAULT_COUNTRY,
  resolveCountryLabel,
} from '@/lib/constants'
import { getApiErrorMessage } from '@/services/api'
import { requestOpenCookieSettings } from '@/lib/cookieConsent'
import { cn } from '@/lib/utils'
import {
  formatPhoneInput,
  formatPhoneNumber,
  isValidPhoneNumber,
  normalizePhoneNumber,
} from '@/lib/phone'

interface CompanyState {
  company_name: string
  address: string
  country: string
  city: string
  postal_code: string
  phone: string
  email: string
  website: string
  siret: string
  vat_number: string
  bank_name: string
  iban: string
  bic: string
  monthly_revenue_objective: string
}

const EMPTY_COMPANY: CompanyState = {
  company_name: '',
  address: '',
  country: DEFAULT_COUNTRY,
  city: '',
  postal_code: '',
  phone: '',
  email: '',
  website: '',
  siret: '',
  vat_number: '',
  bank_name: '',
  iban: '',
  bic: '',
  monthly_revenue_objective: '',
}

const DEFAULT_PRIMARY = '#FF5A1F'
const DEFAULT_SECONDARY = '#1f2937'
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const URL_RE = /^https?:\/\/.+/i
const HEX6_RE = /^#?[0-9A-Fa-f]{6}$/
// IBAN: country + check digits + BBAN (15–34 chars total after spaces stripped).
const IBAN_RE = /^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/
// BIC/SWIFT: 8 or 11 alphanumeric characters.
const BIC_RE = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/

const fieldInputClass =
  'h-10 rounded-xl bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none placeholder:font-normal placeholder:text-[#6c7285]'
const sectionCardClass =
  'overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white shadow-[0_8px_16px_-4px_rgba(38,45,66,0.1),0_1px_4px_-4px_rgba(38,45,66,0.05)]'
const sectionCardBodyClass = 'flex flex-col gap-4 px-6 pb-6 pt-4'
const saveButtonClass =
  'h-10 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-4 text-sm font-medium text-white shadow-none hover:bg-[#e6511c]'
const uploadButtonClass =
  'inline-flex h-auto shrink-0 items-center gap-2 overflow-hidden rounded-xl border border-dashed border-[#ff5a1f] bg-white p-3 text-xs font-medium leading-none text-[#ff5a1f] shadow-none hover:bg-[#fff1ea] hover:text-[#ff5a1f]'
const selectTriggerClass = cn(
  'h-10 rounded-xl bg-[#f4f5f8] px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] shadow-none data-[placeholder]:font-normal [&>span]:font-normal',
  FORM_CONTROL_FOCUS_CLASS
)

function normalizeBankCode(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase()
}

/** Display IBAN in groups of 4 (e.g. DE75 5121 0800 1245 1261 99). */
function formatIban(value: string): string {
  const raw = normalizeBankCode(value).slice(0, 34)
  return raw.replace(/(.{4})/g, '$1 ').trim()
}

function FieldLabel({
  htmlFor,
  required,
  className,
  children,
}: {
  htmlFor?: string
  required?: boolean
  className?: string
  children: ReactNode
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className={cn(
        'flex items-center gap-1 text-xs font-medium leading-4 text-[#171a20]',
        className
      )}
    >
      {children}
      {required && <span className='text-[#ec221f]'> *</span>}
    </Label>
  )
}

function SectionCard({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <section className={sectionCardClass}>
      <header className='flex items-center gap-2 px-0 pb-2 pt-6 pr-6'>
        <div className='flex min-w-0 flex-1 items-center border-l-4 border-[#ff5a1f] px-6 py-1'>
          <h2 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
            {title}
          </h2>
        </div>
      </header>
      <div className={sectionCardBodyClass}>{children}</div>
    </section>
  )
}

const legalDocumentTileClass =
  'flex min-h-16 min-w-0 items-center gap-3 rounded-2xl border border-[#e7e9ef] bg-white p-3 text-left shadow-[0_1px_4px_-1px_rgba(38,45,66,0.05),0_1px_8px_-1px_rgba(38,45,66,0.1)] transition-colors hover:bg-[#f4f5f8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#ff5a1f]'

function LegalDocumentTile({
  title,
  icon: Icon,
  to,
  onClick,
}: {
  title: string
  icon: LucideIcon
  to?: string
  onClick?: () => void
}) {
  const content = (
    <>
      <span className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-[#f4f5f8]'>
        <Icon className='size-5 text-[#262d42]' aria-hidden />
      </span>
      <span className='min-w-0 flex-1 text-sm font-medium leading-[1.4] text-[#171a20]'>
        {title}
      </span>
      <ChevronRight className='size-5 shrink-0 text-[#262d42]' aria-hidden />
    </>
  )

  return to ? (
    <Link to={to} className={legalDocumentTileClass}>
      {content}
    </Link>
  ) : (
    <button type='button' onClick={onClick} className={legalDocumentTileClass}>
      {content}
    </button>
  )
}

function normalizeHex(value: string, fallback = DEFAULT_PRIMARY): string {
  const raw = value.trim()
  if (!HEX6_RE.test(raw)) return fallback
  return raw.startsWith('#') ? raw.toUpperCase() : `#${raw.toUpperCase()}`
}

function hexWithoutHash(value: string): string {
  return value.replace(/^#/, '').toUpperCase()
}

function isBlank(value: string): boolean {
  return value.trim() === ''
}

// 2.11.21 + 2.11.22 — Legal & contact and branding on one General Information page.
export function GeneralInformationPanel() {
  const { t } = useTranslation('settings')
  const {
    data: companyData,
    isLoading: companyLoading,
    isError: companyError,
  } = useCompanySettings()
  const {
    data: brandingData,
    isLoading: brandingLoading,
    isError: brandingError,
  } = useBranding()
  const updateCompany = useUpdateCompanySettings()
  const updateBranding = useUpdateBranding()

  const [companyState, setCompanyState] = useState<CompanyState>(EMPTY_COMPANY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY)
  const [hexDraft, setHexDraft] = useState(hexWithoutHash(DEFAULT_PRIMARY))
  const [secondaryColor, setSecondaryColor] = useState(DEFAULT_SECONDARY)
  const [secondaryHexDraft, setSecondaryHexDraft] = useState(
    hexWithoutHash(DEFAULT_SECONDARY)
  )
  const [logo, setLogo] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const colorInput = useRef<HTMLInputElement>(null)
  const secondaryColorInput = useRef<HTMLInputElement>(null)

  const isLoading = companyLoading || brandingLoading
  const isError = companyError || brandingError
  const isSaving = updateCompany.isPending || updateBranding.isPending

  useEffect(() => {
    const c = companyData?.data
    if (!c) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCompanyState({
      company_name: c.company_name ?? '',
      address: c.address ?? '',
      country: resolveCountryLabel(c.country),
      city: c.city ?? '',
      postal_code: c.postal_code ?? '',
      phone: formatPhoneNumber(c.phone),
      email: c.email ?? '',
      website: c.website ?? '',
      siret: c.siret ?? '',
      vat_number: c.vat_number ?? '',
      bank_name: c.bank_name ?? '',
      iban: formatIban(c.iban ?? ''),
      bic: c.bic ?? '',
      monthly_revenue_objective:
        c.monthly_revenue_objective != null
          ? String(c.monthly_revenue_objective)
          : '',
    })
  }, [companyData])

  useEffect(() => {
    const branding = brandingData?.data
    const nextPrimary = branding?.primary_color
      ? normalizeHex(branding.primary_color)
      : DEFAULT_PRIMARY
    const nextSecondary = branding?.secondary_color
      ? normalizeHex(branding.secondary_color)
      : DEFAULT_SECONDARY
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPrimaryColor(nextPrimary)
    setHexDraft(hexWithoutHash(nextPrimary))
    setSecondaryColor(nextSecondary)
    setSecondaryHexDraft(hexWithoutHash(nextSecondary))
    if (!logo) {
      setLogoPreview(branding?.logo_url ?? null)
    }
  }, [brandingData, logo])

  const setCompany = (patch: Partial<CompanyState>) =>
    setCompanyState(s => ({ ...s, ...patch }))

  function onLogoChange(f: File | null) {
    setLogo(f)
    setErrors(s => ({ ...s, logo: '' }))
    setLogoPreview(
      f ? URL.createObjectURL(f) : (brandingData?.data?.logo_url ?? null)
    )
  }

  function applyHexDraft(raw: string) {
    setHexDraft(raw.replace(/^#/, '').toUpperCase())
    if (HEX6_RE.test(raw.trim())) {
      setPrimaryColor(normalizeHex(raw))
    }
  }

  function applySecondaryHexDraft(raw: string) {
    setSecondaryHexDraft(raw.replace(/^#/, '').toUpperCase())
    if (HEX6_RE.test(raw.trim())) {
      setSecondaryColor(normalizeHex(raw, secondaryColor))
    }
  }

  async function save() {
    const next: Record<string, string> = {}
    if (isBlank(companyState.company_name))
      next.company_name = t('company.validation.companyNameRequired')
    if (isBlank(companyState.address))
      next.address = t('company.validation.addressRequired')
    if (isBlank(companyState.country))
      next.country = t('company.validation.countryRequired')
    if (isBlank(companyState.city))
      next.city = t('company.validation.cityRequired')
    if (isBlank(companyState.postal_code))
      next.postal_code = t('company.validation.postalCodeRequired')
    if (isBlank(companyState.phone))
      next.phone = t('company.validation.phoneRequired')
    else if (!isValidPhoneNumber(companyState.phone))
      next.phone = t('company.validation.phoneInvalid')
    if (isBlank(companyState.email))
      next.email = t('company.validation.emailRequired')
    if (isBlank(companyState.siret))
      next.siret = t('company.validation.siretRequired')
    if (companyState.email && !EMAIL_RE.test(companyState.email))
      next.email = t('company.validation.emailInvalid')
    if (companyState.website && !URL_RE.test(companyState.website))
      next.website = t('company.validation.websiteInvalid')
    const iban = normalizeBankCode(companyState.iban)
    if (iban && !IBAN_RE.test(iban))
      next.iban = t('company.validation.ibanInvalid')
    const bic = normalizeBankCode(companyState.bic)
    if (bic && !BIC_RE.test(bic)) next.bic = t('company.validation.bicInvalid')
    const objectiveRaw = companyState.monthly_revenue_objective.trim()
    const objective = objectiveRaw === '' ? null : Number(objectiveRaw)
    if (objective !== null && (Number.isNaN(objective) || objective < 0))
      next.monthly_revenue_objective = t('company.validation.objectiveInvalid')
    if (!logoPreview && !logo) next.logo = t('branding.validation.logoRequired')
    setErrors(next)
    if (Object.keys(next).length) return

    try {
      await Promise.all([
        updateCompany.mutateAsync({
          company_name: companyState.company_name.trim(),
          address: companyState.address.trim(),
          phone: normalizePhoneNumber(companyState.phone),
          email: companyState.email.trim(),
          website: companyState.website.trim() || null,
          siret: companyState.siret.trim(),
          vat_number: companyState.vat_number.trim(),
          country: companyState.country.trim(),
          city: companyState.city.trim(),
          postal_code: companyState.postal_code.trim(),
          bank_name: companyState.bank_name.trim() || null,
          iban: iban || null,
          bic: bic || null,
          monthly_revenue_objective: objective,
        }),
        updateBranding.mutateAsync({
          primary_color: primaryColor,
          secondary_color: secondaryColor,
          logo: logo ?? undefined,
        }),
      ])
      setLogo(null)
      toast({
        variant: 'success',
        title: t('company.toast.saved'),
      })
    } catch (e) {
      toast({ variant: 'destructive', title: getApiErrorMessage(e) })
    }
  }

  if (isLoading)
    return (
      <div className='space-y-5'>
        <Skeleton className='h-[264px] rounded-2xl' />
        <Skeleton className='h-[640px] rounded-2xl' />
        <Skeleton className='h-[240px] rounded-2xl' />
      </div>
    )
  if (isError)
    return (
      <section className={sectionCardClass}>
        <div className='px-6 py-8 text-center text-sm text-destructive'>
          {t('generalInformation.loadError')}
        </div>
      </section>
    )

  const field = (
    key: keyof CompanyState,
    label: string,
    opts?: {
      type?: string
      placeholder?: string
      required?: boolean
      className?: string
      inputClassName?: string
      formatValue?: (value: string) => string
    }
  ) => (
    <div className={cn('flex flex-col gap-1', opts?.className)}>
      <FieldLabel htmlFor={`co-${key}`} required={opts?.required}>
        {label}
      </FieldLabel>
      <Input
        id={`co-${key}`}
        type={opts?.type ?? 'text'}
        value={companyState[key]}
        placeholder={opts?.placeholder}
        required={opts?.required}
        onChange={e => {
          const value = opts?.formatValue
            ? opts.formatValue(e.target.value)
            : e.target.value
          setCompany({ [key]: value } as Partial<CompanyState>)
          setErrors(s => ({ ...s, [key]: '' }))
        }}
        className={cn(
          fieldInputClass,
          opts?.inputClassName,
          errors[key] ? 'border border-destructive bg-white' : undefined
        )}
      />
      {errors[key] && <p className='text-sm text-destructive'>{errors[key]}</p>}
    </div>
  )

  return (
    <div className='flex min-h-0 flex-1 flex-col'>
      <div className='space-y-5 pb-24'>
        <SectionCard title={t('branding.title')}>
          <div className='flex flex-col gap-4'>
            <div className='flex items-center gap-4'>
              <div
                className={cn(
                  'flex shrink-0 items-center justify-center overflow-hidden rounded-[13.875px] border-[1.156px] border-dashed bg-[#fbfbfc] text-[#3d434e]',
                  logoPreview
                    ? 'w-auto max-w-[150px] border-transparent p-1'
                    : errors.logo
                      ? 'size-[74px] border-destructive'
                      : 'size-[74px] border-[#e7e9ef]'
                )}
              >
                {logoPreview ? (
                  <img
                    src={logoPreview}
                    alt={t('branding.logoPreviewAlt')}
                    className='h-full w-full object-contain'
                  />
                ) : (
                  <ImageIcon className='size-[23.125px]' aria-hidden />
                )}
              </div>
              <div className='flex shrink-0 flex-col items-start justify-center gap-1.5'>
                <FieldLabel
                  htmlFor='company-logo'
                  required
                  className='w-full whitespace-nowrap [&>span]:text-[#d63b33]'
                >
                  {t('branding.logo')}
                </FieldLabel>
                <input
                  id='company-logo'
                  ref={fileInput}
                  type='file'
                  accept='image/png,image/jpeg'
                  className='hidden'
                  onChange={e => onLogoChange(e.target.files?.[0] ?? null)}
                />
                <Button
                  type='button'
                  variant='outline'
                  className={uploadButtonClass}
                  onClick={() => fileInput.current?.click()}
                >
                  <Camera className='size-4' />
                  {t('branding.addPhoto')}
                </Button>
                {errors.logo && (
                  <p className='text-xs text-destructive'>{errors.logo}</p>
                )}
              </div>
            </div>

            <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
              <div className='flex flex-col gap-1'>
                <FieldLabel htmlFor='brand-color-hex' required>
                  {t('branding.primaryColor')}
                </FieldLabel>
                <div className='flex items-end gap-2'>
                  <button
                    type='button'
                    aria-label={t('branding.primaryColor')}
                    className='size-10 shrink-0 rounded-xl border border-[#e7e9ef]'
                    style={{ backgroundColor: primaryColor }}
                    onClick={() => colorInput.current?.click()}
                  />
                  <input
                    ref={colorInput}
                    type='color'
                    value={normalizeHex(primaryColor)}
                    onChange={e => {
                      const next = normalizeHex(e.target.value)
                      setPrimaryColor(next)
                      setHexDraft(hexWithoutHash(next))
                    }}
                    className='sr-only'
                  />
                  <div className='relative min-w-0 flex-1'>
                    <Hash
                      className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#171a20]'
                      aria-hidden
                    />
                    <Input
                      id='brand-color-hex'
                      value={hexDraft}
                      onChange={e => applyHexDraft(e.target.value)}
                      onBlur={() => {
                        const next = normalizeHex(hexDraft, primaryColor)
                        setPrimaryColor(next)
                        setHexDraft(hexWithoutHash(next))
                      }}
                      className={cn(fieldInputClass, 'pl-10')}
                      spellCheck={false}
                      autoComplete='off'
                    />
                  </div>
                </div>
              </div>

              <div className='flex flex-col gap-1'>
                <FieldLabel htmlFor='brand-secondary-color-hex' required>
                  {t('branding.secondaryColor')}
                </FieldLabel>
                <div className='flex items-end gap-2'>
                  <button
                    type='button'
                    aria-label={t('branding.secondaryColor')}
                    className='size-10 shrink-0 rounded-xl border border-[#e7e9ef]'
                    style={{ backgroundColor: secondaryColor }}
                    onClick={() => secondaryColorInput.current?.click()}
                  />
                  <input
                    ref={secondaryColorInput}
                    type='color'
                    value={normalizeHex(secondaryColor)}
                    onChange={e => {
                      const next = normalizeHex(e.target.value)
                      setSecondaryColor(next)
                      setSecondaryHexDraft(hexWithoutHash(next))
                    }}
                    className='sr-only'
                  />
                  <div className='relative min-w-0 flex-1'>
                    <Hash
                      className='pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#171a20]'
                      aria-hidden
                    />
                    <Input
                      id='brand-secondary-color-hex'
                      value={secondaryHexDraft}
                      onChange={e => applySecondaryHexDraft(e.target.value)}
                      onBlur={() => {
                        const next = normalizeHex(
                          secondaryHexDraft,
                          secondaryColor
                        )
                        setSecondaryColor(next)
                        setSecondaryHexDraft(hexWithoutHash(next))
                      }}
                      className={cn(fieldInputClass, 'pl-10')}
                      spellCheck={false}
                      autoComplete='off'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard title={t('company.title')}>
          <div className='flex flex-col gap-4'>
            {field('company_name', t('company.fields.companyName'), {
              required: true,
            })}
            {field('address', t('company.fields.address'), {
              required: true,
            })}
            <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
              {field('postal_code', t('company.fields.postalCode'), {
                required: true,
              })}
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                {field('city', t('company.fields.city'), {
                  required: true,
                })}
                <div className='flex flex-col gap-1'>
                  <FieldLabel htmlFor='co-country' required>
                    {t('company.fields.country')}
                  </FieldLabel>
                  <Select
                    value={companyState.country}
                    onValueChange={value => {
                      setCompany({ country: value })
                      setErrors(s => ({ ...s, country: '' }))
                    }}
                  >
                    <SelectTrigger
                      id='co-country'
                      className={cn(
                        selectTriggerClass,
                        'justify-between',
                        errors.country
                          ? 'border border-destructive bg-white'
                          : undefined
                      )}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(country => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.country && (
                    <p className='text-sm text-destructive'>{errors.country}</p>
                  )}
                </div>
              </div>
            </div>
            <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
              {field('phone', t('company.fields.phone'), {
                type: 'tel',
                required: true,
                formatValue: formatPhoneInput,
              })}
              {field('email', t('company.fields.email'), {
                type: 'email',
                required: true,
              })}
            </div>
            {field('website', t('company.fields.website'), {
              type: 'url',
              placeholder: t('company.placeholders.website'),
            })}
            <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
              {field('siret', t('company.fields.siret'), {
                required: true,
              })}
              {field('vat_number', t('company.fields.vatNumber'))}
            </div>
            <div className='flex flex-col gap-1'>
              <FieldLabel htmlFor='co-monthly_revenue_objective'>
                {t('company.fields.monthlyRevenueObjective')}
              </FieldLabel>
              <div className='relative'>
                <span className='pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm font-normal leading-[1.4] text-[#171a20]'>
                  €
                </span>
                <Input
                  id='co-monthly_revenue_objective'
                  type='number'
                  step='0.01'
                  min='0'
                  value={companyState.monthly_revenue_objective}
                  placeholder={t(
                    'company.placeholders.monthlyRevenueObjective'
                  )}
                  onChange={e => {
                    setCompany({ monthly_revenue_objective: e.target.value })
                    setErrors(s => ({ ...s, monthly_revenue_objective: '' }))
                  }}
                  className={cn(
                    fieldInputClass,
                    'pl-10',
                    errors.monthly_revenue_objective
                      ? 'border border-destructive bg-white'
                      : undefined
                  )}
                />
              </div>
              {errors.monthly_revenue_objective && (
                <p className='text-sm text-destructive'>
                  {errors.monthly_revenue_objective}
                </p>
              )}
              <p className='text-xs font-medium leading-4 text-[#6c7285]'>
                {t('company.hints.monthlyRevenueObjective')}
              </p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title={t('company.bankDetails.title')}>
          <div className='flex flex-col gap-4'>
            {field('bank_name', t('company.fields.bankName'))}
            <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
              <div className='flex flex-col gap-1'>
                <FieldLabel htmlFor='co-iban'>
                  {t('company.fields.iban')}
                </FieldLabel>
                <Input
                  id='co-iban'
                  value={companyState.iban}
                  placeholder={t('company.placeholders.iban')}
                  onChange={e => {
                    setCompany({ iban: formatIban(e.target.value) })
                    setErrors(s => ({ ...s, iban: '' }))
                  }}
                  className={cn(
                    fieldInputClass,
                    errors.iban
                      ? 'border border-destructive bg-white'
                      : undefined
                  )}
                  spellCheck={false}
                  autoComplete='off'
                />
                {errors.iban && (
                  <p className='text-sm text-destructive'>{errors.iban}</p>
                )}
              </div>
              {field('bic', t('company.fields.bic'), {
                placeholder: t('company.placeholders.bic'),
              })}
            </div>
          </div>
        </SectionCard>

        <SectionCard title={t('company.legalDocuments.title')}>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3'>
            <LegalDocumentTile
              title={t('company.legalDocuments.privacyPolicy')}
              icon={Shield}
              to='/privacy-policy'
            />
            <LegalDocumentTile
              title={t('company.legalDocuments.privacyData')}
              icon={Database}
              to='/privacy-data'
            />
            <LegalDocumentTile
              title={t('company.legalDocuments.cookieSettings')}
              icon={Settings}
              onClick={requestOpenCookieSettings}
            />
          </div>
        </SectionCard>
      </div>

      <footer className='sticky bottom-0 z-20 mt-auto -mx-6 -mb-6 flex h-[72px] items-center justify-end border-t border-[#e7e9ef] bg-white px-6 shadow-[0_-1px_4px_0_rgba(38,45,66,0.05)]'>
        <div className='flex justify-end'>
          <Button
            className={saveButtonClass}
            onClick={() => void save()}
            disabled={isSaving}
          >
            {t('generalInformation.saveChanges')}
          </Button>
        </div>
      </footer>
    </div>
  )
}

export default GeneralInformationPanel
